import React, { useState } from 'react';
import { X, Check, Zap, Star } from 'lucide-react';
import { scanApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

interface PricingModalProps {
  onClose: () => void;
  onSignIn?: () => void;
  highlight?: 'keywords' | 'scans' | 'export' | 'tlds';
}

const FREE_FEATURES = [
  'Top 10 keywords (30d)',
  '3 scans per day',
  'Up to 20 TLDs per scan',
  'Watchlist (10 domains)',
  'Brand risk analysis',
];

const PREMIUM_FEATURES = [
  'All 100 keywords + 5 time periods',
  'Unlimited scans',
  'All 40+ TLDs per scan',
  'Unlimited watchlist',
  'Brand risk analysis',
  'CSV / JSON export',
  'Price history charts',
  'Priority support',
];

const HIGHLIGHT_MAP: Record<string, string> = {
  keywords: 'All 100 keywords + 5 time periods',
  scans: 'Unlimited scans',
  export: 'CSV / JSON export',
  tlds: 'All 40+ TLDs per scan',
};

const PricingModal: React.FC<PricingModalProps> = ({ onClose, onSignIn, highlight }) => {
  const { isAuthenticated } = useAuth();
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('yearly');
  const [loading, setLoading] = useState(false);

  const monthlyPrice = 9;
  const yearlyPrice = 79;
  const displayPrice = billing === 'monthly' ? monthlyPrice : Math.round(yearlyPrice / 12);

  const handleUpgrade = async () => {
    if (!isAuthenticated) {
      onSignIn?.();
      return;
    }
    setLoading(true);
    try {
      const { url } = await scanApi.createCheckoutSession(billing);
      window.location.href = url;
    } catch {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl z-10 overflow-hidden">
        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors z-10">
          <X className="h-4 w-4" />
        </button>

        {/* Header */}
        <div className="px-8 pt-8 pb-6 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-1">
            <Zap className="h-5 w-5 text-blue-500" />
            <h2 className="text-xl font-bold text-gray-900">Go Premium</h2>
          </div>
          <p className="text-sm text-gray-500">Unlock the full power of DomBase for serious domain hunters.</p>

          {/* Billing toggle */}
          <div className="flex items-center gap-3 mt-4">
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setBilling('monthly')}
                className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${billing === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBilling('yearly')}
                className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${billing === 'yearly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Yearly
              </button>
            </div>
            {billing === 'yearly' && (
              <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Save 27%</span>
            )}
          </div>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-2 gap-0 divide-x divide-gray-100">
          {/* Free */}
          <div className="p-6">
            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Free</p>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-gray-900">$0</span>
                <span className="text-sm text-gray-400">/mo</span>
              </div>
            </div>
            <ul className="space-y-2.5">
              {FREE_FEATURES.map(f => (
                <li key={f} className="flex items-start gap-2 text-sm text-gray-600">
                  <Check className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Premium */}
          <div className="p-6 bg-gray-50 relative">
            <div className="absolute top-4 right-4">
              <span className="inline-flex items-center gap-1 bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                <Star className="h-3 w-3 fill-white" /> Premium
              </span>
            </div>
            <div className="mb-4">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">Premium</p>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-gray-900">${displayPrice}</span>
                <span className="text-sm text-gray-400">/mo</span>
              </div>
              {billing === 'yearly' && (
                <p className="text-xs text-gray-400 mt-0.5">${yearlyPrice} billed annually</p>
              )}
            </div>
            <ul className="space-y-2.5 mb-6">
              {PREMIUM_FEATURES.map(f => {
                const isHighlighted = highlight && HIGHLIGHT_MAP[highlight] === f;
                return (
                  <li key={f} className={`flex items-start gap-2 text-sm ${isHighlighted ? 'text-blue-700 font-semibold' : 'text-gray-700'}`}>
                    <Check className={`h-4 w-4 flex-shrink-0 mt-0.5 ${isHighlighted ? 'text-blue-500' : 'text-blue-400'}`} />
                    {f}
                    {isHighlighted && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">unlocks this</span>}
                  </li>
                );
              })}
            </ul>
            <button
              onClick={handleUpgrade}
              disabled={loading}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2.5 rounded-lg text-sm transition-colors disabled:opacity-50"
            >
              {loading ? 'Redirecting…' : isAuthenticated ? 'Upgrade to Premium' : 'Sign in & Upgrade'}
            </button>
          </div>
        </div>

        <div className="px-8 py-3 bg-gray-50 border-t border-gray-100">
          <p className="text-xs text-center text-gray-400">
            Secure checkout via Stripe · Cancel anytime · No hidden fees
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingModal;
